import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, Megaphone, ShieldCheck, Sparkles, Star } from 'lucide-react';
import { FeaturedEnquiryDialog } from './FeaturedEnquiryDialog';

const ROT_KEY = 'ciq_feat_rot';
export const FEATURED_REF_KEY = 'ciq_featured_ref';

// Each visit starts one slot later, so every featured vendor gets equal first-position exposure.
const rotate = (items) => {
  if (!items.length) return items;
  const n = Number(localStorage.getItem(ROT_KEY) || 0);
  localStorage.setItem(ROT_KEY, String(n + 1));
  return Array.from({ length: Math.min(4, items.length) }, (_, i) => items[(n + i) % items.length]);
};

const track = (ids, event) => axios.post('/featured-solutions/track', { ids, event }).catch(() => {});

const Card = ({ v, paid, Logo, logoSrc, labels }) => (
  <Link to={v.kind === 'emr' ? `/emr/${v.id}` : `/vendor/${v.id}`} data-testid={`home-featured-${v.id}`}
    onClick={() => { if (paid) { sessionStorage.setItem(FEATURED_REF_KEY, v.id); track([v.id], 'click'); } }}
    className="group relative flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-orange-300 transition-[box-shadow,transform,border-color] duration-200">
    {paid && (
      <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-orange-700" data-testid={`home-featured-badge-${v.id}`}>
        <Sparkles className="h-3 w-3" /> Featured
      </span>
    )}
    <div className={`flex items-center gap-3 mb-3 ${paid ? 'pr-16' : ''}`}>
      <Logo vendor={v} src={logoSrc(v.logo_url)} />
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-slate-900 truncate">{v.name}</h3>
        <p className="text-xs text-slate-500 truncate">{labels[v.category] || 'Solution'}</p>
      </div>
    </div>
    <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">{v.description}</p>
    <div className="mt-4 flex items-center justify-between">
      {v.total_reviews > 0 ? (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-700">
          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />{Number(v.average_rating || 0).toFixed(1)}
          <span className="text-slate-400">({v.total_reviews})</span>
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><ShieldCheck className="w-3.5 h-3.5" /> Verified</span>
      )}
      <span className="text-xs font-semibold text-orange-600 inline-flex items-center gap-0.5 group-hover:gap-1.5 transition-[gap]">View <ArrowRight className="w-3.5 h-3.5" /></span>
    </div>
  </Link>
);

export const FeaturedSolutions = ({ fallback, Logo, logoSrc, labels }) => {
  const [paid, setPaid] = React.useState(null);
  const [enquiry, setEnquiry] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    axios.get('/featured-solutions')
      .then((r) => { if (!cancelled) setPaid(rotate(r.data.items || [])); })
      .catch(() => { if (!cancelled) setPaid([]); });
    return () => { cancelled = true; };
  }, []);

  const isPaid = !!paid?.length;
  React.useEffect(() => {
    if (!isPaid || !ref.current) return undefined;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { track(paid.map((v) => v.id), 'view'); obs.disconnect(); }
    }, { threshold: 0.4 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [isPaid, paid]);

  const items = isPaid ? paid : fallback;
  if (paid === null || !items.length) return null;
  return (
    <section ref={ref} className="py-12 md:py-16 bg-white" data-testid="home-featured-solutions">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8 md:mb-10">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-orange-600 mb-2">Popular on ClinicIQ</p>
            <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-slate-900">Featured solutions</h2>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <button type="button" onClick={() => setEnquiry(true)} data-testid="home-featured-get-featured"
              className="text-sm font-medium text-slate-500 hover:text-orange-700 inline-flex items-center gap-1.5 transition-colors">
              <Megaphone className="w-4 h-4" /> Vendors: get featured here
            </button>
            <Link to="/marketplace" className="text-sm font-semibold text-orange-600 hover:text-orange-700 inline-flex items-center gap-1" data-testid="home-featured-view-all">
              View all solutions <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((v) => <Card key={v.id} v={v} paid={isPaid} Logo={Logo} logoSrc={logoSrc} labels={labels} />)}
        </div>
      </div>
      <FeaturedEnquiryDialog open={enquiry} onClose={() => setEnquiry(false)} />
    </section>
  );
};
